import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * Configurable POS settings card. Available to any authenticated user.
 * Currently exposes: customer_clear_timeout (2–120 seconds).
 */
export default function PosSettingsSection() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [timeoutInput, setTimeoutInput] = useState("5");

    useEffect(() => {
        api.get("/pos-settings")
            .then((r) => {
                setSettings(r.data);
                setTimeoutInput(String(r.data.customer_clear_timeout ?? 5));
            })
            .catch((e) => {
                toast.error(e?.response?.data?.detail || "Αποτυχία φόρτωσης");
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const n = parseInt(timeoutInput, 10);
        if (Number.isNaN(n) || n < 2 || n > 120) {
            toast.error("Δώστε τιμή από 2 έως 120 δευτερόλεπτα");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put("/pos-settings", {
                customer_clear_timeout: n,
            });
            setSettings(data);
            toast.success(`Χρονικό όριο: ${n} δευτ.`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αποθήκευσης");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-sm text-[#5C5F66]">
                Φόρτωση...
            </div>
        );
    }
    if (!settings) return null;

    return (
        <div className="space-y-6 max-w-3xl" data-testid="pos-settings-section">
            <div className="border-2 border-[#0A0A0A] bg-white">
                <div className="bg-[#0A0A0A] text-white px-4 py-2 flex items-center gap-2">
                    <ClockCounterClockwise size={16} weight="bold" />
                    <span className="text-sm font-bold uppercase tracking-[0.2em]">
                        ΡΥΘΜΙΣΕΙΣ POS
                    </span>
                </div>

                <form onSubmit={handleSave} className="p-6 space-y-6">
                    <div className="border-l-4 border-[#FF3B30] pl-4 space-y-2">
                        <Label className="text-sm font-black uppercase tracking-[0.15em] text-[#0A0A0A]">
                            ΧΡΟΝΙΚΟ ΟΡΙΟ ΠΕΛΑΤΗ (ΔΕΥΤΕΡΟΛΕΠΤΑ)
                        </Label>
                        <p className="text-xs text-[#5C5F66] leading-relaxed">
                            Πόσα δευτερόλεπτα θα παραμένει ενεργός ο πελάτης στο POS
                            μετά τη σάρωση της κάρτας του, αν δεν προστεθεί κανένα
                            είδος στο καλάθι. Μετά από αυτό το διάστημα, ο πελάτης
                            αφαιρείται αυτόματα και εμφανίζεται το εικονίδιο «ΤΕΛΟΣ
                            ΧΡΟΝΟΥ».
                        </p>
                        <div className="flex items-end gap-3 flex-wrap pt-2">
                            <div className="flex-1 min-w-[200px]">
                                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66] mb-1">
                                    Δευτερόλεπτα (2 – 120)
                                </div>
                                <Input
                                    type="number"
                                    min={2}
                                    max={120}
                                    step={1}
                                    value={timeoutInput}
                                    onChange={(e) => setTimeoutInput(e.target.value)}
                                    data-testid="pos-timeout-input"
                                    className="rounded-none h-14 text-2xl font-black font-mono border-2 border-[#0A0A0A] w-full"
                                    placeholder="π.χ. 5"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="pos-timeout-save"
                                className="rounded-none h-14 px-8 bg-[#0A0A0A] hover:bg-[#333] text-white font-bold tracking-wider"
                            >
                                {saving ? "…" : "ΑΠΟΘΗΚΕΥΣΗ"}
                            </Button>
                        </div>
                        <div className="text-[10px] text-[#5C5F66] font-mono mt-1">
                            Τρέχουσα ρύθμιση:{" "}
                            <span className="font-bold text-[#0A0A0A]">
                                {settings.customer_clear_timeout} δευτ.
                            </span>
                            {" · "}
                            Προτεινόμενες τιμές: 3, 5, 8, 10, 15
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
